import React, { useState, useEffect } from "react";
import { Input, Button, Popover, Checkbox, Form, Spin, notification } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  PlusCircleOutlined,
  CheckCircleFilled,
  CloseOutlined,
  LeftOutlined,
  CheckOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";

import { COLORS } from "../../constants";
import { doGetAllLabels, doCreateLabel, doUpdateLabel, doDeleteLabel } from "../../modules/label/stores/thunk";
import { selectAllLabels } from "../../modules/label/stores/slice";
import "./LabelComponent.scss";

const LabelComponent = ({ labelIds = [], setLabelIds, isEdit = true, canEdit = true }) => {
  const [action, setAction] = useState("list"); //list, new, edit
  const [visiblePopover, setVisiblePopover] = useState(false);
  const [created, setCreated] = useState(false);
  const [searchValue, setSearchValue] = useState();
  const [currentColor, setCurrentColor] = useState(COLORS[0]);
  const [currentLabel, setCurrentLabel] = useState();
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const allLabels = selectAllLabels(useSelector(state => state));
  const stateLabels = allLabels.filter(l => labelIds.includes(l._id));
  const { isFetching } = useSelector(state => state.label);

  useEffect(() => {
    dispatch(doGetAllLabels({}));
  }, []);

  useEffect(() => {
    if (created) {
      setLabelIds([...labelIds, allLabels[0]?._id]);
      setCreated(false);
    }
  }, [allLabels]);

  const handleAddLabel = () => {
    setAction("list");
  };

  const handleSelectedLabel = label => {
    let labelIdsT = [...labelIds];
    if (labelIdsT.includes(label._id)) {
      labelIdsT = labelIdsT.filter(l => l !== label._id);
    } else {
      labelIdsT.push(label._id);
    }
    const stateLabelsT = allLabels.filter(l => labelIdsT.includes(l._id));
    if (stateLabelsT.length <= 3) {
      setLabelIds(labelIdsT);
    } else {
      notification.error({
        message: "Maximum number of labels is 3",
        placement: "bottomRight",
      });
    }
  };

  const handleEditLabel = label => {
    setAction("item");
    setCurrentLabel(label);
    setCurrentColor(label.color);
    form.setFieldsValue({ name: label.name });
  };

  const handleNewLabel = () => {
    setAction("item");
    setCurrentLabel(null);
    form.resetFields();
  };

  const handleOnFinish = async params => {
    if (currentLabel) {
      dispatch(doUpdateLabel({ ...currentLabel, ...params, color: currentColor }));
    } else {
      dispatch(doCreateLabel({ ...params, color: currentColor }));
      setCreated(true);
    }
    setAction("list");
  };

  const handleOnDelete = label => {
    dispatch(doDeleteLabel(label._id));
    setAction("list");
    setCurrentLabel(null);
    form.resetFields();
  };

  const contentList = () => {
    const searchLabels = searchValue ? allLabels.filter(l => l.name.includes(searchValue)) : allLabels;

    return (
      <Spin spinning={isFetching}>
        <div className="PopoverContent List">
          <Input placeholder="Search" onChange={e => setSearchValue(e.target.value)} />
          <div className="TitleItem">Labels</div>
          {searchLabels.map(l => {
            return (
              <div key={l._id} className="Item">
                <Checkbox checked={stateLabels.includes(l)} onChange={() => handleSelectedLabel(l)} />
                <div className="LabelItem" style={{ backgroundColor: `#${l.color}20` }}>
                  <span style={{ backgroundColor: `#${l.color}` }} /> {l.name}
                </div>
                {canEdit && <Button size="small" icon={<EditOutlined />} onClick={() => handleEditLabel(l)} />}
              </div>
            );
          })}
          {canEdit && (
            <Button disabled={stateLabels.length >= 3} className="BtnNewLabel" onClick={handleNewLabel} block>
              Create a New Label <PlusCircleOutlined />
            </Button>
          )}
        </div>
      </Spin>
    );
  };

  const contentItem = () => {
    return (
      <div className="PopoverContent Item">
        <div className="Header">
          <div className="Btn Back" onClick={() => setAction("list")}>
            <LeftOutlined />
          </div>
          <div className="Title">{currentLabel ? "Edit Label" : "Create New Label"}</div>
          <div className="Btn Close" onClick={() => setVisiblePopover(false)}>
            <CloseOutlined />
          </div>
        </div>
        <Form form={form} scrollToFirstError hideRequiredMark colon={false} onFinish={handleOnFinish}>
          <Form.Item
            label="Label Name"
            value={currentLabel?.name}
            name="name"
            rules={[
              { required: true, message: "Required" },
              {
                max: 13,
                message: "Value should be less than 13 character",
              },
            ]}>
            <Input />
          </Form.Item>
          <div className="Colors">
            {COLORS.map((color, index) => {
              return (
                <div
                  className={`LabelColor ${color === currentColor ? "selected" : ""}`}
                  key={index}
                  onClick={() => setCurrentColor(color)}
                  style={{ backgroundColor: `#${color}` }}>
                  <CheckOutlined />
                </div>
              );
            })}
          </div>
          <div className="Actions">
            {currentLabel && (
              <Button key="delete" danger onClick={() => handleOnDelete(currentLabel)} className="BtnDeleteLabel">
                Delete <DeleteOutlined />
              </Button>
            )}
            <Button
              type="primary"
              htmlType="submit"
              block
              className={currentLabel ? "BtnUpdateLabel" : "BtnCreateLabel"}>
              {currentLabel ? "Update" : "Create"} <CheckCircleFilled />
            </Button>
          </div>
        </Form>
      </div>
    );
  };

  const PopoverContent = () => {
    if (action === "list") {
      return contentList();
    }
    if (action === "item") {
      return contentItem();
    }
  };

  return (
    <Spin spinning={isFetching}>
      <div className="LabelComponent">
        {stateLabels.map(l => (
          <div key={l._id} className="LabelItem" style={{ backgroundColor: `#${l.color}20` }}>
            <span style={{ backgroundColor: `#${l.color}` }} /> {l.name}
          </div>
        ))}
        {isEdit && (
          <Popover
            placement="topLeft"
            content={PopoverContent()}
            trigger="click"
            open={visiblePopover}
            onOpenChange={setVisiblePopover}>
            <Button size="small" icon={<PlusOutlined />} onClick={handleAddLabel} />
          </Popover>
        )}
      </div>
    </Spin>
  );
};

export default LabelComponent;
